import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { ProductService } from '../../../../services/product.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription, zip } from 'rxjs';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { tap, filter, map, switchMap } from 'rxjs/operators';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InvCountTeardown } from '../../../../models/inv-count-teardown';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftStatus } from '@shared/models/worked-shift';
import { ShiftContext } from '../../../../models/shift-context';
import { saveDebounce } from '@shared/helpers/save-debounce';

type TeardownForm = FormGroup<{
    id: FormControl<number>;
    productId: FormControl<number>;
    teardowns: FormControl<number>;
}>;

@Component({
    selector: 'app-teardowns-scrap',
    templateUrl: './teardowns-scrap.component.html',
    styleUrls: ['./teardowns-scrap.component.scss'],
})
export class TeardownsScrapComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    addScrapForm = new FormGroup({
        productId: new FormControl<number>(null, Validators.required),
        teardowns: new FormControl<number>(0, Validators.required)
    });

    addIdScrapForm = new FormGroup({
        productId: new FormControl<number>(null, Validators.required),
        teardowns: new FormControl<number>(0, Validators.required)
    });

    identifiedScrapProducts: SelectItem[] = [];
    scrapProducts: SelectItem[] = [];
    context: ShiftContext;
    siteSubinventoryId = new BehaviorSubject<number>(null);

    scrapProductId: number;
    teardownProductId: number;

    identifiedScrapItems: TeardownForm[] = [];
    scrapItems: TeardownForm[] = [];
    teardownItem: TeardownForm | null;

    formSubscriptions: Subscription[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();

    readonly _teardownScrapItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Teardown/Scrap Item',
        detail: 'Fields are required.',
    };

    readonly _teardownScrapItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Teardown/Scrap Item Created',
        detail: '',
    };

    readonly _teardownScrapItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Teardown/Scrap Item Deleted',
        detail: '',
    };

    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit() {
        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap((context) => {
            this.context = context;

            if (context.status !== ShiftStatus.Open) {
                this.addScrapForm.disable();
                this.addIdScrapForm.disable();
                this.identifiedScrapItems.forEach(i => i.disable({ emitEvent: false }));
                this.teardownItem?.disable({ emitEvent: false });
                this.scrapItems.forEach(i => i.disable({ emitEvent: false }));
            }
            else {
                this.addScrapForm.enable();
                this.addIdScrapForm.enable();
                this.identifiedScrapItems.forEach((form) => form.enable({ emitEvent: false }));
                this.teardownItem?.enable({ emitEvent: false });
                this.scrapItems.forEach(i => i.enable({ emitEvent: false }));
            }
        }));

        const teardownInventoryObservable = combineLatest([
            this.inventoryService.CurrentInvTeardowns.pipe(filter(t => !!t)),
            this.productService.TeardownProductsForSubinventory(this.siteSubinventoryId)
        ]).pipe(switchMap(([currentItems, products]) => {
            this.identifiedScrapProducts = products.idScrap.map((p) => ({
                value: p.id,
                label: p.label,
            }));

            this.scrapProducts = products.scrap.map((p) => ({
                value: p.id,
                label: p.label,
            }));

            const teardownProductId = products.teardowns[0]?.id;

            this.formSubscriptions.forEach(s => s.unsubscribe());

            this.identifiedScrapItems = currentItems
                .filter(item => products.idScrap.some(s => s.id === item.productId))
                .map(teardownScrapItem => this.createTeardownForm(teardownScrapItem));

                this.scrapItems = currentItems
                .filter(item => products.scrap.some(s => s.id === item.productId))
                .map(scrapItem => this.createTeardownForm(scrapItem));

            const teardownItem = currentItems.find(item => teardownProductId === item.productId);
            this.teardownItem = teardownItem ? this.createTeardownForm(teardownItem) : null;

            // pre-create teardown and scrap items if they don't exist

            let teardownObs: Observable<any>;

            if (this.teardownItem || !(this.context.status === ShiftStatus.Open))
                teardownObs = of(true);
            else {
                teardownObs = this.createTeardownItem({
                    invCountId: this.invCountId,
                    productId: teardownProductId,
                    teardowns: 0
                }).pipe(tap(form => {
                    this.teardownItem = form;
                }))
            }

            return teardownObs;
        }));

        this.subscriptions = [
            combineLatest([ siteObservable, teardownInventoryObservable ])
                .pipe(this.activityService.Wait('Teardowns/Scrap'))
                .subscribe()
        ];

    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.formSubscriptions.forEach((s) => s.unsubscribe());
        this.formSubscriptions = [];
        this.identifiedScrapItems = [];
        this.teardownItem = null;
        this.scrapItems = [];
    }

    updateTeardownItem(form: TeardownForm) {
        const teardownScrapItem = {
            id: form.controls.id.value,
            invCountId: this.invCountId,
            productId: form.controls.productId.value,
            teardowns: +form.controls.teardowns.value
        }

        this.inventoryService.updateInvCountTeardown(teardownScrapItem).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Teardown/Scrap Item Updated',
                detail: ''
            });
        });
    }

    private createTeardownItem(item: InvCountTeardown): Observable<TeardownForm> {
        return this.inventoryService.createInvCountTeardownCount(item)
            .pipe(map(created => this.createTeardownForm(created)))
    }

    addScrap() {
        if (!this.addScrapForm.valid) {
            this.addScrapForm.markAllAsTouched();
            this.messageService.add(this._teardownScrapItemNotCreatedMsg);
        } else {
            const entry: InvCountTeardown = {
                invCountId: this.invCountId,
                productId: this.addScrapForm.controls.productId.value,
                teardowns: this.addScrapForm.controls.teardowns.value
            };
            this.createTeardownItem(entry).subscribe(form => {
                this.scrapItems = [ ...this.scrapItems, form];

                this.addScrapForm.reset({
                    productId: null,
                    teardowns: 0
                });

                this.messageService.add(this._teardownScrapItemCreatedMsg);
            });
        }
    }

    addIdScrap() {
        if (!this.addIdScrapForm.valid) {
            this.addIdScrapForm.markAllAsTouched();
            this.messageService.add(this._teardownScrapItemNotCreatedMsg);
        } else {
            const entry: InvCountTeardown = {
                invCountId: this.invCountId,
                productId: this.addIdScrapForm.controls.productId.value,
                teardowns: this.addIdScrapForm.controls.teardowns.value
            };
            this.createTeardownItem(entry).subscribe(form => {
                this.identifiedScrapItems = [ ...this.identifiedScrapItems, form];

                this.addIdScrapForm.reset({
                    productId: null,
                    teardowns: 0
                });

                this.messageService.add(this._teardownScrapItemCreatedMsg);
            });
        }
    }

    createTeardownForm(count: InvCountTeardown): TeardownForm {
        const form: TeardownForm = new FormGroup({
            id: new FormControl<number>(count.id),
            productId: new FormControl<number>(count.productId, Validators.required),
            teardowns: new FormControl<number>(count.teardowns, Validators.required)
        });

        if (this.context?.status !== ShiftStatus.Open)
            form.disable({ emitEvent: false });
        else
            form.enable({ emitEvent: false });

        this.formSubscriptions.push(form.valueChanges
            .pipe(saveDebounce(this.shutdown))
            .subscribe(() => {
                this.updateTeardownItem(form);
            }));

        return form;
    }

    deleteScrapItem(item: TeardownForm) {
        this.inventoryService.deleteInvCountTeardown(item.controls.id.value).subscribe(() => {
            this.scrapItems = this.scrapItems
                .filter(i => i !== item);
            this.messageService.add(this._teardownScrapItemDeletedMsg);
        });
    }

    deleteIdScrapItem(item: TeardownForm) {
        this.inventoryService.deleteInvCountTeardown(item.controls.id.value).subscribe(() => {
            this.identifiedScrapItems = this.identifiedScrapItems
                .filter(i => i !== item);
            this.messageService.add(this._teardownScrapItemDeletedMsg);
        });
    }

    rowTrackBy(_i: number, item: TeardownForm): number {
        return item.controls.id.value;
    }
}
